import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Pencil, UserX, UserCheck, Trash2 } from 'lucide-react';
import { useEmployes, useDesactiverEmploye, useReactiverEmploye, useSupprimerEmploye } from '../hooks/useEmployes';
import { useEmployesStore } from '../store/employes.store';
import { useProjets } from '../../administration/hooks/useProjets';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';
import { useAuthStore } from '../../auth/presentation/store/authStore';
import { IconButton } from '../../../shared/components/ui/IconButton';

const ROLES_ADMIN = ['admin-organisation', 'super-admin', 'admin-national', 'admin-diocesain'];

const LIMITE = 20;

export function EmployesListePage() {
  const { filtresListe, setFiltres } = useEmployesStore();
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [erreurSuppression, setErreurSuppression] = useState<Record<string, string>>({});

  const userRole = useAuthStore(s => s.user?.role ?? '');
  const estAdmin = ROLES_ADMIN.includes(userRole);

  useEffect(() => { setPage(1); setSelection(new Set()); }, [filtresListe.actif, filtresListe.recherche, filtresListe.idProjet]);

  const { data: resultat, isLoading, isError } = useEmployes({
    actif: filtresListe.actif,
    recherche: filtresListe.recherche || undefined,
    idProjet: filtresListe.idProjet || undefined, // '__AUCUN__' transmis tel quel au backend
    page,
    limite: LIMITE,
  });

  const { data: projets } = useProjets({ actif: true });
  const listeProjets = (projets as any[]) ?? [];

  const desactiver = useDesactiverEmploye();
  const reactiver = useReactiverEmploye();
  const supprimer = useSupprimerEmploye();
  const confirm = useConfirm();

  const employes = (resultat as any)?.items ?? (Array.isArray(resultat) ? resultat : []);
  const total = (resultat as any)?.total ?? employes.length;
  const totalPages = Math.ceil(total / LIMITE);

  const tousSelectionnes = employes.length > 0 && employes.every((e: any) => selection.has(e.idEmploye));
  const toggleTous = () => {
    if (tousSelectionnes) setSelection(new Set());
    else setSelection(new Set(employes.map((e: any) => e.idEmploye)));
  };
  const toggleUn = (id: string) => {
    const s = new Set(selection);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelection(s);
  };

  const desactiverSelection = async () => {
    const ok = await confirm({
      title: `Désactiver ${selection.size} employé(s) ?`,
      message: 'Ces employés seront marqués inactifs. Vous pourrez les réactiver ultérieurement.',
      variant: 'danger',
      labelConfirm: 'Désactiver',
    });
    if (!ok) return;
    await Promise.all([...selection].map(id => desactiver.mutateAsync(id)));
    setSelection(new Set());
  };

  const supprimerSelection = async () => {
    const ok = await confirm({
      title: `Supprimer définitivement ${selection.size} employé(s) ?`,
      message: 'Cette action est irréversible. Seuls les employés sans bulletin ni opposition seront supprimés. Les autres resteront inchangés.',
      variant: 'danger',
      labelConfirm: 'Supprimer définitivement',
    });
    if (!ok) return;
    const erreurs: string[] = [];
    await Promise.allSettled([...selection].map(async id => {
      try {
        await supprimer.mutateAsync(id);
      } catch (err: any) {
        erreurs.push(err?.response?.data?.message || err?.message || `Erreur pour ${id}`);
      }
    }));
    setSelection(new Set());
    if (erreurs.length > 0) {
      setErreurSuppression(prev => ({ ...prev, __masse__: erreurs.join(' — ') }));
    }
  };

  const handleSupprimer = async (idEmploye: string, nom: string, prenoms: string) => {
    const ok = await confirm({
      title: 'Supprimer définitivement ?',
      message: `Cette action est irréversible. L'employé ${nom} ${prenoms} et toutes ses données associées seront supprimés définitivement.`,
      variant: 'danger',
      labelConfirm: 'Supprimer définitivement',
    });
    if (!ok) return;
    try {
      await supprimer.mutateAsync(idEmploye);
      setErreurSuppression(prev => { const n = { ...prev }; delete n[idEmploye]; return n; });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erreur lors de la suppression';
      setErreurSuppression(prev => ({ ...prev, [idEmploye]: msg }));
    }
  };

  const anciennete = (dateEmbauche: string) => {
    const debut = new Date(dateEmbauche);
    const now = new Date();
    const mois = (now.getFullYear() - debut.getFullYear()) * 12 + (now.getMonth() - debut.getMonth());
    const ans = Math.floor(mois / 12);
    const m = mois % 12;
    return ans > 0 ? `${ans} an${ans > 1 ? 's' : ''} ${m > 0 ? `${m} mois` : ''}` : `${m} mois`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content">Employés</h1>
        <div className="flex gap-2">
          <Link to="/paie/employes/nouveau" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium">
            + Ajouter
          </Link>
          <Link to="/paie/employes/import" className="border border-border text-content-muted px-4 py-2 rounded hover:bg-surface-raised text-sm">
            Importer CSV
          </Link>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <input
          type="text"
          placeholder="Rechercher..."
          value={filtresListe.recherche}
          onChange={e => setFiltres({ recherche: e.target.value })}
          className="border border-border rounded px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {listeProjets.length > 0 && (
          <select
            value={filtresListe.idProjet ?? ''}
            onChange={e => setFiltres({ idProjet: e.target.value || undefined })}
            className="border border-border rounded px-3 py-2 text-sm"
          >
            <option value="">Tous les projets</option>
            <option value="__AUCUN__">Aucun projet</option>
            {listeProjets.map((p: any) => (
              <option key={p.idProjet} value={p.idProjet}>{p.nom}</option>
            ))}
          </select>
        )}
        <select
          value={filtresListe.actif === undefined ? '' : String(filtresListe.actif)}
          onChange={e => setFiltres({ actif: e.target.value === '' ? undefined : e.target.value === 'true' })}
          className="border border-border rounded px-3 py-2 text-sm"
        >
          <option value="">Tous</option>
          <option value="true">Actifs</option>
          <option value="false">Inactifs</option>
        </select>
      </div>

      {isLoading && <p className="text-content-subtle text-sm">Chargement...</p>}
      {isError && <p className="text-danger text-sm">Erreur lors du chargement.</p>}

      {!isLoading && employes.length === 0 && (
        <div className="text-center py-12 text-content-subtle">
          <p>Aucun employé trouvé.</p>
        </div>
      )}

      {selection.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-sm font-medium text-amber-800">{selection.size} employé(s) sélectionné(s)</span>
          <button
            onClick={desactiverSelection}
            disabled={desactiver.isPending}
            className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-50"
          >
            Désactiver la sélection
          </button>
          {estAdmin && (
            <button
              onClick={supprimerSelection}
              disabled={supprimer.isPending}
              className="px-3 py-1.5 bg-red-900 text-white text-xs font-semibold rounded hover:bg-red-800 disabled:opacity-50"
            >
              Supprimer définitivement
            </button>
          )}
          {erreurSuppression.__masse__ && (
            <span className="text-xs text-danger">{erreurSuppression.__masse__}</span>
          )}
          <button onClick={() => { setSelection(new Set()); setErreurSuppression({}); }} className="text-xs text-amber-700 hover:underline">
            Annuler
          </button>
        </div>
      )}

      {employes.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-raised">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={tousSelectionnes} onChange={toggleTous} className="rounded border-border" />
                </th>
                {['Nom', 'Prénom', 'Fonction', 'Projet', 'Salaire base', 'Ancienneté', 'Statut'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-content-subtle uppercase">{h}</th>
                ))}
                <th className="px-4 py-3 text-xs font-medium text-content-subtle uppercase"></th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {employes.map((e: any) => (
                <tr key={e.idEmploye} className={`hover:bg-surface-raised ${selection.has(e.idEmploye) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selection.has(e.idEmploye)} onChange={() => toggleUn(e.idEmploye)} className="rounded border-border" />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-content">{e.nom}</td>
                  <td className="px-4 py-3 text-sm text-content-muted">{e.prenoms}</td>
                  <td className="px-4 py-3 text-sm text-content-muted">{e.fonction}</td>
                  <td className="px-4 py-3 text-sm text-content-muted">
                    {e.nomProjet
                      ? <span className="px-2 py-1 bg-accent-light text-accent rounded text-xs font-medium">{e.nomProjet}</span>
                      : <span className="text-content-subtle text-xs">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-sm text-content">{e.salaireBase.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3 text-sm text-content-muted">{anciennete(e.dateEmbauche)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${e.actif ? 'bg-success-light text-success' : 'bg-surface-overlay text-content-subtle'}`}>
                      {e.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-1 items-center">
                      <IconButton icon={Eye} label="Voir" href={`/paie/employes/${e.idEmploye}`} />
                      <IconButton icon={Pencil} label="Modifier" href={`/paie/employes/${e.idEmploye}/modifier`} />
                      {e.actif ? (
                        <IconButton
                          icon={UserX}
                          label="Désactiver"
                          variant="danger"
                          disabled={desactiver.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Désactiver ${e.nom} ${e.prenoms} ?`,
                              message: 'Cet employé sera marqué inactif. Vous pourrez le réactiver ultérieurement.',
                              variant: 'danger',
                              labelConfirm: 'Désactiver',
                            });
                            if (ok) desactiver.mutate(e.idEmploye);
                          }}
                        />
                      ) : (
                        <IconButton
                          icon={UserCheck}
                          label="Réactiver"
                          variant="success"
                          disabled={reactiver.isPending}
                          onClick={() => reactiver.mutate(e.idEmploye)}
                        />
                      )}
                      {estAdmin && (
                        <IconButton
                          icon={Trash2}
                          label="Supprimer définitivement"
                          variant="danger"
                          disabled={supprimer.isPending}
                          onClick={() => handleSupprimer(e.idEmploye, e.nom, e.prenoms)}
                        />
                      )}
                    </div>
                    {estAdmin && erreurSuppression[e.idEmploye] && (
                      <p className="text-xs text-danger mt-1">{erreurSuppression[e.idEmploye]}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <p className="text-sm text-content-subtle">
            {total} employé(s) — page {page} / {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-border rounded text-sm disabled:opacity-40 hover:bg-surface-raised"
            >
              ← Précédent
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-border rounded text-sm disabled:opacity-40 hover:bg-surface-raised"
            >
              Suivant →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
